import React, { useEffect, useMemo, useRef } from 'react';
import {
  createChart, AreaSeries, LineSeries, ColorType, createSeriesMarkers,
} from 'lightweight-charts';

/* A search-interest curve is read for its shape over five years, so the chart is
 * given room and drawn with a light hand: hairline grid, no last-value badge, no
 * axis chrome competing with the line itself.
 *
 * Three series, on two scales:
 *
 *   raw            Google's 0-100 index, the thing everyone recognises.
 *   seasonal-adj   the same weeks with the ISO-week factor divided out. This is
 *                  the curve detection actually decides on, and it is drawn
 *                  because the two disagreeing is exactly how a term ends up on
 *                  the page looking unremarkable - Beats (BR) read 67 against a
 *                  raw 95th percentile of 80, and only cleared the bar once
 *                  adjusted. A reader should be able to see that for themselves.
 *   volume         estimated searches per week, on its own left scale because it
 *                  is an absolute count and the other two are an index.
 */

const GRID = '#1a1f27';
const TEXT = '#6b7481';
const CROSS = '#3b4653';
const RAW = '#58a6ff';
const ADJ = '#d2a8ff';
/* Volume is context, not the subject. Kept faint deliberately: at full strength
 * the filled area reads as the main series and the two index lines disappear
 * into it, which is backwards - the break is in the index, and volume only says
 * how much the move is worth. */
const VOL_LINE = 'rgba(63, 185, 80, 0.45)';
const VOL_TOP = 'rgba(63, 185, 80, 0.20)';
const VOL_BOTTOM = 'rgba(63, 185, 80, 0.01)';
const MARK = '#d29922';

const iso = (d) => String(d).slice(0, 10);

export default function SeriesChart({ points, markers = [], height = 320 }) {
  const holder = useRef(null);
  const chart = useRef(null);
  const raw = useRef(null);
  const adj = useRef(null);
  const vol = useRef(null);
  const marks = useRef(null);

  /* Callers build this inline - markers={episodes.map(...)} - so it is a new
   * array on every render. Keyed on contents instead, or the effect below reruns
   * constantly and fitContent() throws away the reader's zoom each time. */
  const markerKey = JSON.stringify(markers);

  useEffect(() => {
    if (!holder.current) return undefined;

    chart.current = createChart(holder.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: TEXT,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
        // The library's own watermark. It is someone else's brand sitting on
        // our data, and the licence does not require it.
        attributionLogo: false,
      },
      grid: { vertLines: { color: 'transparent' }, horzLines: { color: GRID } },
      rightPriceScale: { borderVisible: false },
      leftPriceScale: { borderVisible: false, visible: true },
      timeScale: { borderVisible: false, fixLeftEdge: true, fixRightEdge: true },
      crosshair: {
        vertLine: { color: CROSS, width: 1, style: 2, labelBackgroundColor: '#1a212b' },
        horzLine: { color: CROSS, width: 1, style: 2, labelBackgroundColor: '#1a212b' },
      },
      height,
      autoSize: true,
    });

    // Volume first so it sits behind both lines rather than over them.
    vol.current = chart.current.addSeries(AreaSeries, {
      priceScaleId: 'left',
      lineColor: VOL_LINE,
      topColor: VOL_TOP,
      bottomColor: VOL_BOTTOM,
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat: { type: 'volume' },
    });

    adj.current = chart.current.addSeries(LineSeries, {
      color: ADJ,
      lineWidth: 1,
      lineStyle: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    raw.current = chart.current.addSeries(LineSeries, {
      color: RAW,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: false,
    });

    // v5 moved markers off the series onto their own primitive; series.setMarkers
    // no longer exists. Created once here and fed by the data effect below.
    marks.current = createSeriesMarkers(raw.current, []);

    return () => {
      chart.current?.remove();
      chart.current = null;
      raw.current = null;
      adj.current = null;
      vol.current = null;
      marks.current = null;
    };
  }, [height]);

  useEffect(() => {
    if (!raw.current || !points) return;

    // Google reports nothing for a week below its threshold, and a null is not
    // a zero: charting it as one would invent a collapse that never happened.
    const has = (v) => v !== null && v !== undefined;
    const pick = (key) => points
      .filter((p) => has(p[key]))
      .map((p) => ({ time: iso(p.week_end), value: Number(p[key]) }));

    const rawData = pick('value');
    raw.current.setData(rawData);
    adj.current?.setData(pick('value_deseason'));

    // Ads runs four years against Trends' five and lags up to six weeks, so
    // volume is absent for most series and blank at the front even when present.
    // An empty set hides the series rather than drawing a flat zero line.
    const volData = pick('estimated_volume');
    vol.current?.setData(volData);
    chart.current?.priceScale('left').applyOptions({ visible: volData.length > 0 });

    // Sorted because the library requires ascending time, and filtered to marks
    // the curve actually reaches: a break week that fell in a gap Google never
    // reported has no bar to sit under.
    const first = rawData.length ? rawData[0].time : null;
    const last = rawData.length ? rawData[rawData.length - 1].time : null;
    marks.current?.setMarkers(
      markers
        .map((m) => iso(m.time || ''))
        .filter((t) => t && first && t >= first && t <= last)
        .sort()
        .map((time) => ({
          time,
          position: 'belowBar',
          color: MARK,
          shape: 'arrowUp',
          text: (markers.find((m) => iso(m.time) === time) || {}).text || '',
        }))
    );
    chart.current?.timeScale().fitContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, markerKey]);

  // Google Ads omits low-volume keywords entirely, so about half of all series
  // have no absolute figure at any point in their history. Naming a line in the
  // legend that the chart never draws reads as missing data rather than as data
  // that does not exist, so the label goes when the line does.
  const hasVolume = useMemo(
    () => (points || []).some(
      (p) => p.estimated_volume !== null && p.estimated_volume !== undefined),
    [points]
  );

  return (
    <div className="tw-chart-wrap">
      <div className="tw-chart" ref={holder} style={{ height }} />
      {/* One group, read left to right like anything else. This was split to
        * the two ends of the chart to mirror the axes, which put a quarter of
        * the page between three labels that describe one picture - and the
        * (lhs)/(rhs) tags already say which scale each line belongs to. */}
      <div className="tw-legend">
        {hasVolume && (
          <span><i style={{ background: VOL_LINE }} />est. searches/week (lhs)</span>
        )}
        <span><i style={{ background: RAW }} />search interest (rhs)</span>
        <span><i style={{ background: ADJ }} />seasonally adjusted (rhs)</span>
      </div>
    </div>
  );
}
